import { useEffect, useRef, useState } from "react";

// Halloween only: spiders wander around the screen (meander, pause, turn, leave) and small flocks of bats
// swoop about erratically. Some spiders drop in on a thread first. Pure decoration (pointer-events: none).
const rand = (a, b) => a + Math.random() * (b - a);
const SIZE = 34;
const BAT_SIZE = 44;

const SpiderSvg = () => (
  <svg viewBox="0 0 64 64" width={SIZE} height={SIZE} className="hw-spider-body" aria-hidden="true">
    <g stroke="#160F1E" strokeWidth="3" strokeLinecap="round" fill="none">
      <g className="hw-legs-l">
        <path d="M26 30 L12 18 L6 22" /><path d="M24 34 L8 30 L2 34" /><path d="M24 38 L8 42 L4 50" /><path d="M26 42 L14 52 L12 60" />
      </g>
      <g className="hw-legs-r">
        <path d="M38 30 L52 18 L58 22" /><path d="M40 34 L56 30 L62 34" /><path d="M40 38 L56 42 L60 50" /><path d="M38 42 L50 52 L52 60" />
      </g>
    </g>
    <ellipse cx="32" cy="40" rx="10" ry="12" fill="#160F1E" />
    <circle cx="32" cy="24" r="7" fill="#160F1E" />
    <circle cx="29.5" cy="22" r="1.4" fill="#FF7E33" /><circle cx="34.5" cy="22" r="1.4" fill="#FF7E33" />
  </svg>
);

const BatSvg = () => (
  <svg viewBox="0 0 64 64" width={BAT_SIZE} height={BAT_SIZE} className="hw-bat-body" aria-hidden="true">
    <g fill="#1B1424">
      <path className="hw-wing-l" d="M30 30 C22 22 12 20 2 24 C8 26 10 30 8 34 C14 32 18 34 20 38 C24 36 27 38 28 42 Z" />
      <path className="hw-wing-r" d="M34 30 C42 22 52 20 62 24 C56 26 54 30 56 34 C50 32 46 34 44 38 C40 36 37 38 36 42 Z" />
      <ellipse cx="32" cy="35" rx="5" ry="9" />
      <circle cx="32" cy="25" r="5" />
      <path d="M28 22 L27 15 L31 20 Z M36 22 L37 15 L33 20 Z" />
    </g>
    <circle cx="30" cy="24" r="1.1" fill="#FF7E33" /><circle cx="34" cy="24" r="1.1" fill="#FF7E33" />
  </svg>
);

const vw = () => window.innerWidth;
const vh = () => window.innerHeight;

const randomTarget = (x, y, minD, maxD) => {
  for (let i = 0; i < 8; i++) {
    const a = rand(0, Math.PI * 2), d = rand(minD, maxD);
    const tx = x + Math.cos(a) * d, ty = y + Math.sin(a) * d;
    if (tx > 24 && tx < vw() - 24 && ty > 60 && ty < vh() - 90) return { tx, ty };
  }
  return { tx: rand(40, vw() - 40), ty: rand(80, vh() - 110) };
};

const edgeStart = (side = Math.floor(rand(0, 4)), size = SIZE) => {
  if (side === 0) return { x: -size, y: rand(80, vh() - 120) };
  if (side === 1) return { x: vw() + size, y: rand(80, vh() - 120) };
  if (side === 2) return { x: rand(40, vw() - 40), y: -size };
  return { x: rand(40, vw() - 40), y: vh() + size };
};

const exitTarget = (x, y, size) => {
  const d = [x, vw() - x, y, vh() - y];
  const i = d.indexOf(Math.min(...d));
  return [{ tx: -size * 2, ty: y }, { tx: vw() + size * 2, ty: y }, { tx: x, ty: -size * 2 }, { tx: x, ty: vh() + size * 2 }][i];
};

const makeSpider = (now) => {
  const drop = Math.random() < 0.4;
  const start = drop ? { x: rand(60, vw() - 60), y: 0 } : edgeStart();
  const s = {
    id: `s${now}${Math.random()}`, kind: "spider", size: SIZE, drop, x: start.x, y: start.y, angle: Math.PI / 2,
    speed: rand(55, 95), turn: 3.2, minD: 90, maxD: 360, pauses: true,
    mode: drop ? "dropping" : "walk", modeUntil: now + (drop ? 2700 : 0), dropDepth: rand(140, Math.max(200, vh() * 0.4)),
    leaveAt: now + rand(35000, 65000), leaving: false, bob: 0,
  };
  s.target = randomTarget(s.x, s.y, s.minD, s.maxD);
  if (!drop) s.angle = Math.atan2(s.target.ty - s.y, s.target.tx - s.x);
  return s;
};

const makeBat = (now, side, stay) => {
  const start = edgeStart(side, BAT_SIZE);
  const b = {
    id: `b${now}${Math.random()}`, kind: "bat", size: BAT_SIZE, x: start.x, y: start.y, angle: 0,
    speed: rand(150, 240), turn: 5.5, minD: 120, maxD: 520, pauses: false,
    mode: "walk", modeUntil: 0, leaveAt: now + stay + rand(-1500, 1500), leaving: false, bob: rand(0, Math.PI * 2),
  };
  b.target = randomTarget(b.x, b.y, b.minD, b.maxD);
  b.angle = Math.atan2(b.target.ty - b.y, b.target.tx - b.x);
  return b;
};

export default function HalloweenCritters() {
  const [ids, setIds] = useState([]);
  const nodes = useRef(new Map());
  const sim = useRef([]);

  useEffect(() => {
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return undefined;
    let raf, spiderTimer, batTimer, last = performance.now();

    const add = (ents) => {
      sim.current.push(...ents);
      setIds((prev) => [...prev, ...ents.map((e) => ({ id: e.id, kind: e.kind }))]);
    };
    const spawnSpider = () => {
      const now = performance.now();
      add([makeSpider(now)]);
      if (Math.random() < 0.3) spiderTimer = setTimeout(spawnSpider, rand(4000, 9000));
    };
    const spawnBats = () => {
      const now = performance.now(), side = Math.floor(rand(0, 4)), stay = rand(6000, 11000);
      add(Array.from({ length: Math.floor(rand(2, 5)) }, () => makeBat(now, side, stay)));
      batTimer = setTimeout(spawnBats, rand(12000, 28000));
    };

    const step = (now) => {
      const dt = Math.min(0.05, (now - last) / 1000); last = now;
      const gone = [];
      for (const s of sim.current) {
        const el = nodes.current.get(s.id);
        if (s.mode === "dropping") {
          const p = Math.min(1, 1 - (s.modeUntil - now) / 2700);
          s.y = s.dropDepth * (p < 0.85 ? p / 0.85 : 1);
          if (now >= s.modeUntil) { s.mode = "walk"; s.target = randomTarget(s.x, s.y, s.minD, s.maxD); }
        } else if (s.mode === "pause") {
          if (now >= s.modeUntil) { s.mode = "walk"; s.target = randomTarget(s.x, s.y, s.minD, s.maxD); }
        } else {
          if (!s.leaving && now > s.leaveAt) { s.leaving = true; s.target = exitTarget(s.x, s.y, s.size); }
          const dx = s.target.tx - s.x, dy = s.target.ty - s.y, dist = Math.hypot(dx, dy);
          if (dist < 10) {
            if (s.leaving) { gone.push(s.id); continue; }
            if (s.pauses && Math.random() < 0.35) { s.mode = "pause"; s.modeUntil = now + rand(700, 2400); }
            else s.target = randomTarget(s.x, s.y, s.minD, s.maxD);
          } else {
            const want = Math.atan2(dy, dx);
            let diff = want - s.angle; while (diff > Math.PI) diff -= Math.PI * 2; while (diff < -Math.PI) diff += Math.PI * 2;
            s.angle += Math.max(-s.turn * dt, Math.min(s.turn * dt, diff));
            const v = s.speed * (s.kind === "spider" && Math.abs(diff) > 1 ? 0.45 : 1);
            s.x += Math.cos(s.angle) * v * dt; s.y += Math.sin(s.angle) * v * dt;
          }
        }
        if (el) {
          const deg = s.mode === "dropping" ? 0 : (s.angle * 180) / Math.PI + 90;
          const bobY = s.kind === "bat" ? Math.sin(now / 140 + s.bob) * 5 : 0;
          el.style.transform = `translate(${s.x - s.size / 2}px, ${s.y - s.size / 2 + bobY}px) rotate(${deg}deg)`;
          el.classList.toggle("hw-walking", s.mode === "walk");
          const thread = el.querySelector(".hw-thread");
          if (thread) {
            thread.style.height = `${s.mode === "dropping" ? Math.max(0, s.y - s.size / 2 + 6) : 0}px`;
            thread.style.opacity = s.mode === "dropping" ? 1 : 0;
          }
        }
      }
      if (gone.length) {
        const hadSpider = sim.current.some((s) => s.kind === "spider");
        sim.current = sim.current.filter((s) => !gone.includes(s.id));
        setIds((prev) => prev.filter((e) => !gone.includes(e.id)));
        if (hadSpider && !sim.current.some((s) => s.kind === "spider")) spiderTimer = setTimeout(spawnSpider, rand(15000, 40000));
      }
      raf = requestAnimationFrame(step);
    };

    spiderTimer = setTimeout(spawnSpider, rand(4000, 8000));
    batTimer = setTimeout(spawnBats, rand(2500, 6000));
    raf = requestAnimationFrame(step);
    return () => { cancelAnimationFrame(raf); clearTimeout(spiderTimer); clearTimeout(batTimer); sim.current = []; };
  }, []);

  return (
    <>
      {ids.map(({ id, kind }) => (
        <div
          key={id}
          ref={(el) => { if (el) nodes.current.set(id, el); else nodes.current.delete(id); }}
          className={`hw-critter hw-roam ${kind === "bat" ? "hw-bat" : "hw-spider"}`}
          data-testid={`halloween-${kind}`}
          aria-hidden="true"
        >
          {kind === "spider" ? <><span className="hw-thread" /><SpiderSvg /></> : <BatSvg />}
        </div>
      ))}
    </>
  );
}
